import type * as React from 'react';

import { tv } from 'tailwind-variants';

// Design System Button Styles https://www.figma.com/file/MKc5gEytDNyueOf4TXtxZs/Canny-UI-Kit?type=design&node-id=2089-7765&mode=design&t=tvEgLGQectc6UmrL-0
// Design System Button Styles https://www.figma.com/file/MKc5gEytDNyueOf4TXtxZs/Canny-UI-Kit?type=design&node-id=1200-2094&t=UtWrfBBDu2oV9LlV-0
export const buttonVariants = tv({
  slots: {
    base: [
      'inline-flex unstyled-button items-center justify-center gap-2',
      'text-transform-none box-border font-normal',
      'transition-all duration-300 ease-in-out',
      'hover:cursor-pointer shrink-0',
      '[&>svg]:shrink-0 text-base',
      'truncate overflow-hidden focus-visible:focus-ring',
    ],
  },
  variants: {
    variant: {
      contained: {
        base: 'border border-transparent',
      },
      outlined: {
        base: 'bg-background dark:bg-input/30 base-border',
      },
      plain: {
        base: 'bg-transparent border border-transparent',
      },
      ghost: {
        base: 'bg-transparent border border-transparent',
      },
      toggleOff: {
        base: 'bg-transparent dark:bg-input/30 base-border text-foreground',
      },
      toggleOn: {
        base: 'bg-indigo-20 border border-primary text-primary',
      },
      link: {
        base: 'bg-transparent border border-transparent underline hover:no-underline',
      },
      unstyled: {
        base: '',
      },
    },
    fullWidth: {
      true: {
        base: 'w-full',
      },
      false: '',
    },
    color: {
      base: '',
      secondary: '',
      primary: '',
      error: '',
      success: '',
    },
    size: {
      small: {
        base: "h-7 px-2 rounded-sm leading-5 [&_svg:not([class*='size-'])]:!size-3.5",
      },
      default: {
        base: "h-8 px-3 rounded-sm leading-5 [&_svg:not([class*='size-'])]:!size-4",
      },
      large: {
        base: "h-11 px-4 rounded-lg text-lg leading-6 [&_svg:not([class*='size-'])]:!size-4.5",
      },
      xlarge: {
        base: "h-15 px-5 rounded-lg text-xl leading-6 [&_svg:not([class*='size-'])]:!size-4.5",
      },
    },
    responsive: {
      true: '',
      false: '',
    },
    iconOnly: {
      true: {
        base: 'p-0 shrink-0 rounded-sm',
      },
      false: '',
    },
    disabled: {
      true: {
        base: 'opacity-50 text-muted-foreground bg-muted base-border',
      },
      false: '',
    },
    isLoading: {
      true: {
        base: 'hover:cursor-wait',
      },
      false: '',
    },
    isTinted: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Link Variants
    {
      variant: 'link',
      class: {
        base: 'px-0 h-auto',
      },
    },
    // Responsive Variants
    {
      responsive: true,
      size: 'xlarge',
      class: {
        base: 'h-12 md:h-15 text-lg md:text-xl',
      },
    },
    // Color Variants
    {
      variant: 'link',
      color: 'base',
      disabled: false,
      class: {
        base: [
          'text-foreground hover:text-foreground',
          'focus-visible:text-foreground active:text-foreground',
        ],
      },
    },
    {
      variant: 'link',
      color: 'secondary',
      disabled: false,
      class: {
        base: [
          'text-secondary-foreground hover:text-secondary-foreground',
          'focus-visible:text-secondary-foreground active:text-secondary-foreground',
          'hover:text-foreground',
        ],
      },
    },
    {
      variant: 'link',
      color: 'primary',
      disabled: false,
      class: {
        base: [
          'text-primary',
          'hover:text-indigo-100 focus-visible:text-indigo-100',
          'active:text-indigo-110',
        ],
      },
    },
    {
      variant: 'link',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'text-red-90',
          'hover:text-red-100 focus-visible:text-red-100',
          'active:text-red-110',
        ],
      },
    },
    {
      variant: 'link',
      color: 'success',
      disabled: false,
      class: {
        base: [
          'text-green-90',
          'hover:text-green-100 focus-visible:text-green-100',
          'active:text-green-110',
        ],
      },
    },
    {
      variant: 'unstyled',
      class: {
        base: 'h-auto p-0 bg-transparent dark:bg-transparent border-none',
      },
    },
    {
      iconOnly: true,
      size: 'small',
      class: {
        base: 'size-7',
      },
    },
    {
      iconOnly: true,
      size: 'default',
      class: {
        base: 'size-8',
      },
    },
    {
      iconOnly: true,
      size: 'large',
      class: {
        base: 'size-11',
      },
    },
    {
      variant: 'contained',
      color: 'primary',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'bg-primary text-primary-foreground border-primary',
          'hover:bg-indigo-100 focus-visible:bg-indigo-100',
          'active:bg-indigo-110',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'error',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'bg-red-90 text-white border-red-90 text-primary-foreground',
          'hover:bg-red-100 focus-visible:bg-red-100',
          'active:bg-red-110',
        ],
      },
    },
    {
      variant: 'contained',
      color: 'success',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'bg-green-90 text-white border-green-90 text-primary-foreground',
          'hover:bg-green-100 focus-visible:bg-green-100',
          'active:bg-green-110',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'primary',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-foreground',
          'hover:bg-gray-10 hover:dark:bg-input/40 hover:text-foreground hover:border-input-focus',
          'focus-visible:text-foreground focus-visible:border-input-focus',
          'active:bg-gray-20 active:border-gray-80',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'error',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-red-110 border border-red-90',
          'hover:bg-red-40 hover:text-red-90 hover:border-red-90',
          'focus-visible:bg-red-40 focus-visible:text-red-90 focus-visible:border-red-90',
          'active:bg-red-50',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'success',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-green-110 border border-green-90',
          'hover:bg-green-40',
          'focus-visible:bg-green-40',
          'active:bg-green-50',
        ],
      },
    },
    {
      variant: 'ghost',
      color: 'primary',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-foreground',
          'hover:bg-gray-30 hover:border-gray-30',
          'focus-visible:bg-gray-30 focus-visible:border-gray-30',
          'active:bg-gray-40 active:border-gray-40',
        ],
      },
    },
    {
      variant: 'ghost',
      color: 'error',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-red-90',
          'hover:bg-red-40 hover:border-red-40 hover:text-red-90',
          'focus-visible:bg-red-40 focus-visible:border-red-40 focus-visible:text-red-90',
          'active:bg-red-50 active:border-red-50',
        ],
      },
    },
    {
      variant: 'ghost',
      color: 'success',
      disabled: false,
      isTinted: false,
      class: {
        base: [
          'text-green-90',
          'hover:bg-green-40 hover:border-green-40 hover:text-green-90',
          'focus-visible:bg-green-40 focus-visible:border-green-40 focus-visible:text-green-90',
          'active:bg-green-50 active:border-green-50',
        ],
      },
    },
    {
      variant: 'plain',
      iconOnly: false,
      class: {
        base: 'px-0',
      },
    },
    {
      variant: 'plain',
      color: 'primary',
      disabled: false,
      class: {
        base: [
          'text-primary',
          'hover:text-indigo-100 focus-visible:text-indigo-100',
          'active:text-indigo-110',
        ],
      },
    },
    {
      variant: 'plain',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'text-red-90',
          'hover:text-red-100 focus-visible:text-red-100',
          'active:text-red-110',
        ],
      },
    },
    {
      variant: 'plain',
      color: 'success',
      disabled: false,
      class: {
        base: [
          'text-green-90',
          'hover:text-green-100 focus-visible:text-green-100',
          'active:text-green-110',
        ],
      },
    },
    {
      iconOnly: true,
      variant: 'plain',
      color: 'primary',
      disabled: false,
      class: {
        base: [
          'text-gray-110 hover:text-gray-110 focus-visible:text-gray-110',
          'hover:bg-gray-20 dark:hover:bg-gray-40 focus-visible:bg-gray-20 dark:focus-visible:bg-gray-40 focus-visible:text-foreground',
          'active:bg-gray-30 active:text-foreground',
        ],
      },
    },
    {
      iconOnly: true,
      variant: 'plain',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'text-red-90',
          'hover:bg-red-30 focus-visible:bg-red-30 focus-visible:border-red-90',
          'active:bg-red-40 active:border-red-40',
        ],
      },
    },
    {
      disabled: true,
      variant: 'outlined',
      class: {
        base: 'base-border',
      },
    },
    {
      isTinted: true,
      disabled: false,
      class: {
        base: [
          'bg-[var(--company-color)] border-[var(--company-color)]',
          'hover:opacity-80 active:opacity-70',
        ],
      },
    },
    {
      variant: 'toggleOff',
      color: 'primary',
      disabled: false,
      class: {
        base: ['hover:bg-indigo-10 focus-visible:bg-indigo-10 active:bg-indigo-10'],
      },
    },
    {
      variant: 'toggleOff',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'hover:bg-red-40 hover:border-red-90 hover:text-red-90',
          'focus-visible:bg-red-40 focus-visible:border-red-90 focus-visible:text-red-90',
          'active:bg-red-50',
        ],
      },
    },
    {
      variant: 'toggleOff',
      color: 'success',
      disabled: false,
      class: {
        base: [
          'hover:bg-green-40 hover:border-green-90 hover:text-green-90',
          'focus-visible:bg-green-40 focus-visible:border-green-90 focus-visible:text-green-90',
          'active:bg-green-50',
        ],
      },
    },
    {
      variant: 'toggleOn',
      color: 'primary',
      disabled: false,
      class: {
        base: [
          'hover:bg-indigo-30 hover:text-indigo-100',
          'focus-visible:bg-indigo-30 focus-visible:text-indigo-100',
          'active:bg-indigo-30',
        ],
      },
    },
    {
      variant: 'toggleOn',
      color: 'error',
      disabled: false,
      class: {
        base: [
          'hover:bg-red-40 hover:border-red-90 hover:text-red-90',
          'focus-visible:bg-red-40 focus-visible:border-red-90 focus-visible:text-red-90',
          'active:bg-red-50',
        ],
      },
    },
    {
      variant: 'toggleOn',
      color: 'success',
      disabled: false,
      class: {
        base: [
          'hover:bg-green-40 hover:border-green-90 hover:text-green-90',
          'focus-visible:bg-green-40 focus-visible:border-green-90 focus-visible:text-green-90',
          'active:bg-green-50',
        ],
      },
    },
    {
      variant: 'link',
      disabled: true,
      class: {
        base: 'border-transparent bg-transparent dark:bg-transparent',
      },
    },
    {
      variant: 'plain',
      disabled: true,
      class: {
        base: 'border-transparent bg-transparent dark:bg-transparent',
      },
    },
  ],
  defaultVariants: {
    variant: 'contained',
    color: 'primary',
    size: 'default',
    disabled: false,
    loading: false,
    isTinted: false,
    iconOnly: false,
    responsive: false,
  },
});

export const getStyleWithTint = (
  style: React.CSSProperties | undefined,
  tint: string | null | undefined,
): React.CSSProperties | undefined => {
  if (!tint) {
    return style;
  }

  return {
    ...style,
    '--company-color': tint,
  } as React.CSSProperties;
};
